import { useState } from 'react';
import {
  Layers, Cpu, Shield, Wrench, FileText, Search, Mail, ArrowRightLeft,
  ClipboardCheck, Eye, Zap, Clock, ChevronDown, ChevronRight, ExternalLink,
  ScanLine, BarChart3, CheckCircle2, XCircle, Target, Bot, Building2,
  Globe, Package, Download, AlertTriangle, Users, Briefcase, Landmark,
  Link2, FileSearch, CalendarCheck
} from 'lucide-react';

const agents = [
  { num: 1, name: 'G703 Parser', desc: 'Reads SOV PDF → extracts every trade line item automatically', icon: FileText, color: 'text-blue-400' },
  { num: 2, name: 'Document Classifier', desc: 'Reads any uploaded doc → files it to the correct CSI division + folder', icon: Eye, color: 'text-emerald-400' },
  { num: 3, name: 'Warranty Extractor', desc: 'Reads warranty certificates → structured data (dates, coverage, contacts)', icon: Shield, color: 'text-primary' },
  { num: 4, name: 'Chase Email Drafter', desc: 'Writes escalating chase emails per SOP cadence (Day 7 → 14 → 21 → retainage hold)', icon: Mail, color: 'text-violet-400' },
  { num: 5, name: 'Equipment Extractor', desc: 'Scans project documents + drawings → builds complete equipment registry', icon: ScanLine, color: 'text-cyan-400' },
  { num: 6, name: 'Warranty Finder', desc: 'Google searches real OEM warranty pages per make/model → verifies URLs with AI', icon: Search, color: 'text-amber-400', highlight: true },
  { num: 7, name: 'O&M Finder', desc: 'Finds real manufacturer manuals via Google → builds PM schedules + spare parts lists', icon: Wrench, color: 'text-rose-400', highlight: true },
  { num: 8, name: 'VE Reconciler', desc: 'Reviews the equipment list → flags VE\'d out items and duplicates', icon: ArrowRightLeft, color: 'text-teal-400' },
];

const painPoints = [
  { problem: 'O&M Manual Gathering', traditional: 'Waiting 6 months for subs to upload manuals they can\'t find', time: 'Holds up final payment', severity: 'critical' },
  { problem: 'Warranty Tracking', traditional: 'Binder of PDFs nobody checks until something breaks', time: 'Warranties lapse silently', severity: 'critical' },
  { problem: 'Closeout Documentation', traditional: 'Excel trackers, emailing subs, manual filing into folders', time: '3-6 months of chasing', severity: 'high' },
  { problem: 'Drawing Takeoffs', traditional: 'Manual tracing in Bluebeam/Planswift, one sheet at a time', time: '2-4 weeks per building', severity: 'high' },
  { problem: 'Equipment Reconciliation', traditional: 'Compare specs to field conditions by hand at project end', time: 'Items missed entirely', severity: 'medium' },
  { problem: 'Client Turnover', traditional: 'Assembling PDF binders, Excel summaries, and README files manually', time: 'Weeks of formatting', severity: 'medium' },
];

const comparison = [
  { capability: 'Real OEM links from Google Search', us: true, them: false, key: true },
  { capability: 'AI verifies URLs are real (no hallucinated links)', us: true, them: false, key: true },
  { capability: 'AI reads floor plans → room polygons', us: true, them: false },
  { capability: 'AI reads G703 SOV from PDF', us: true, them: false },
  { capability: 'AI files docs to correct division/folder', us: true, them: false },
  { capability: 'AI generates PM schedules per equipment', us: true, them: false },
  { capability: 'AI drafts chase emails per SOP cadence', us: true, them: false },
  { capability: 'IWMS-ready export (Archibus, FM:Systems, Planon)', us: true, them: false, key: true },
  { capability: 'Smart Intake from Day 1 (sub lists, RFIs, drawings, COs)', us: true, them: false },
  { capability: 'VE reconciliation', us: true, them: 'Manual' },
  { capability: 'Cx docs linked to equipment', us: true, them: 'Folder-level' },
  { capability: 'Real-time SSE progress', us: true, them: false },
  { capability: 'Template reuse across projects', us: true, them: 'Limited' },
];

const buyers = [
  { role: 'GCs / CMs', desc: 'Running $10M+ commercial projects — drowning in 26-division closeout binders', icon: Building2, color: 'text-primary' },
  { role: 'Owner\'s Reps', desc: 'Need to verify turnover packages are complete before accepting the building', icon: Briefcase, color: 'text-blue-400' },
  { role: 'Facility Teams', desc: 'Inheriting buildings and need real O&M data, not empty binder tabs', icon: Landmark, color: 'text-emerald-400' },
];

function Section({ children, className = '' }) {
  return <div className={`mb-14 ${className}`}>{children}</div>;
}

function SectionTitle({ icon: Icon, children, color = 'text-primary' }) {
  return (
    <div className="flex items-center gap-3 mb-6">
      {Icon && <Icon className={`w-5 h-5 ${color}`} />}
      <h2 className="text-lg font-heading font-bold uppercase tracking-wider text-foreground">{children}</h2>
      <div className="flex-1 h-px bg-gradient-to-r from-slate-700 to-transparent" />
    </div>
  );
}

function ModuleCard({ title, subtitle, icon: Icon, color, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`border rounded-sm transition-all ${open ? `${color} bg-card` : 'border-border bg-card'}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-4 px-5 py-4 text-left"
        data-testid={`module-card-${title.replace(/\s+/g, '-').toLowerCase()}`}
      >
        <Icon className={`w-5 h-5 shrink-0 ${open ? 'text-primary' : 'text-muted-foreground'}`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-heading font-bold uppercase tracking-wider text-foreground">{title}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
      </button>
      {open && <div className="px-5 pb-5 pt-1 border-t border-border/50">{children}</div>}
    </div>
  );
}

function FeaturePill({ children, accent = false }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-1 text-[11px] font-mono rounded-sm border ${
      accent
        ? 'bg-primary/10 border-primary/30/40 text-primary'
        : 'bg-muted/60 border-border/50 text-foreground/80'
    }`}>
      {children}
    </span>
  );
}

export default function OnePagerPage() {
  return (
    <div className="min-h-screen bg-card" data-testid="about-page">
      <div className="max-w-4xl mx-auto px-6 py-12">

        {/* Hero */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Layers className="w-8 h-8 text-primary" />
            <h1 className="text-3xl sm:text-4xl font-heading font-bold uppercase tracking-wider text-slate-100">
              FMiQ<span className="text-primary">Intel</span>
            </h1>
          </div>
          <p className="text-base sm:text-lg text-foreground/80 max-w-2xl mx-auto leading-relaxed font-medium">
            Automates the 60-90 day closeout grind — from Day 1 intake to owner turnover —
            so your team stops chasing paper and starts closing out projects on time.
          </p>
          <p className="text-sm text-muted-foreground max-w-xl mx-auto mt-3 leading-relaxed">
            AI-powered construction technology that transforms plan digitization and construction
            closeout from months of manual work into hours of supervised automation.
          </p>
          <div className="flex items-center justify-center gap-6 mt-8">
            <div className="text-center">
              <p className="text-2xl font-mono font-bold text-primary">8</p>
              <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">AI Agents</p>
            </div>
            <div className="w-px h-8 bg-secondary" />
            <div className="text-center">
              <p className="text-2xl font-mono font-bold text-emerald-400">5</p>
              <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Modules</p>
            </div>
            <div className="w-px h-8 bg-secondary" />
            <div className="text-center">
              <p className="text-2xl font-mono font-bold text-blue-400">100%</p>
              <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Real Links</p>
            </div>
          </div>
        </div>

        {/* The Problem */}
        <Section>
          <SectionTitle icon={XCircle} color="text-red-400">The Problem with Traditional</SectionTitle>
          <div className="grid gap-2">
            {painPoints.map((p, i) => (
              <div key={i} className={`flex items-start gap-4 px-4 py-3 bg-card border rounded-sm ${
                p.severity === 'critical' ? 'border-red-900/40' : 'border-border'
              }`}>
                <div className="shrink-0 w-40">
                  <p className={`text-xs font-heading uppercase tracking-wider ${
                    p.severity === 'critical' ? 'text-red-400' : 'text-muted-foreground'
                  }`}>{p.problem}</p>
                </div>
                <p className="flex-1 text-xs text-muted-foreground">{p.traditional}</p>
                <div className="shrink-0 text-right">
                  <p className="text-[10px] font-mono text-muted-foreground">{p.time}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-muted-foreground italic pl-4 border-l-2 border-red-900/50">
            The result: closeout packages delivered 6-12 months late, incomplete warranty coverage,
            and facility teams inheriting buildings with no usable operations data.
          </p>
        </Section>

        {/* Key Differentiators */}
        <Section>
          <SectionTitle icon={Target} color="text-primary">What Makes Us Different</SectionTitle>
          <div className="space-y-3">

            {/* Differentiator 1 - REAL LINKS */}
            <div className="relative p-5 bg-gradient-to-br from-[#0d1a0d] to-[#0B1120] border border-emerald-900/40 rounded-sm">
              <div className="absolute top-0 left-0 w-1 h-full bg-emerald-500 rounded-l-sm" />
              <div className="flex items-start gap-4">
                <div className="shrink-0 w-10 h-10 rounded-sm bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-900/30 flex items-center justify-center">
                  <Globe className="w-5 h-5 text-emerald-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-heading font-bold uppercase tracking-wider text-emerald-400 mb-1">
                    Real OEM Links, Not AI Hallucinations
                  </h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Every competitor using AI for closeout docs generates <span className="text-red-400 font-medium">fake placeholder URLs</span>.
                    FMiQ Intel uses <span className="text-emerald-300 font-medium">Google Search (Serper) → GPT-4o verification</span> —
                    every warranty registration page, every O&M manual PDF, every spec sheet is a{' '}
                    <span className="text-white font-medium">real, clickable link</span> from the manufacturer's actual website.
                    The AI can only <em>select</em> from verified search results, never invent.
                  </p>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    <FeaturePill accent>Google Search API</FeaturePill>
                    <FeaturePill accent>GPT-4o URL Verification</FeaturePill>
                    <FeaturePill accent>Zero Hallucinated URLs</FeaturePill>
                  </div>
                </div>
              </div>
            </div>

            {/* Differentiator 2 - DAY 1 TO DAY 90 */}
            <div className="relative p-5 bg-card border border-border rounded-sm">
              <div className="absolute top-0 left-0 w-1 h-full bg-blue-500 rounded-l-sm" />
              <div className="flex items-start gap-4">
                <div className="shrink-0 w-10 h-10 rounded-sm bg-blue-50 dark:bg-blue-950/50 border border-blue-900/30 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-blue-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-heading font-bold uppercase tracking-wider text-blue-400 mb-1">
                    Day 1 Intake → Day 90 Turnover, One Platform
                  </h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Most tools handle either document management OR warranty tracking.
                    FMiQ Intel covers the full lifecycle: sub lists, RFIs, drawing extraction, change order tracking,
                    equipment registry, warranty/O&M gathering, chase emails, gap reports, and the final turnover
                    package (PDF + Excel + ZIP) — all from one dashboard.
                  </p>
                </div>
              </div>
            </div>

            {/* Differentiator 3 - DRAWINGS FEED CLOSEOUT */}
            <div className="relative p-5 bg-card border border-border rounded-sm">
              <div className="absolute top-0 left-0 w-1 h-full bg-cyan-500 rounded-l-sm" />
              <div className="flex items-start gap-4">
                <div className="shrink-0 w-10 h-10 rounded-sm bg-cyan-950/50 border border-cyan-900/30 flex items-center justify-center">
                  <ScanLine className="w-5 h-5 text-cyan-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-heading font-bold uppercase tracking-wider text-cyan-400 mb-1">
                    BUILD &amp; ACTIVATE — Drawings Feed Closeout
                  </h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Drawings live in <span className="text-white font-medium">BUILD</span> (text-layer-first extraction pulls equipment tags, makes, models, rooms, and fixture
                    locations straight from the sheets). Closeout lives in <span className="text-white font-medium">ACTIVATE</span>. One button —{' '}
                    <span className="text-white font-medium">Build Register from Drawings</span> — bridges them: it extracts, groups duplicate fixtures into one asset with
                    multiple locations, imports the equipment register, and auto-finds warranty + PM/O&amp;M data.{' '}
                    <span className="text-white font-medium">Drawings → Assets → Warranties → Turnover</span>, with no manual data entry.
                  </p>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    <FeaturePill accent>One-Click Register</FeaturePill>
                    <FeaturePill>One Trade or Full Set</FeaturePill>
                    <FeaturePill>Owner-Reviewed Before Publish</FeaturePill>
                  </div>
                </div>
              </div>
            </div>

            {/* Differentiator 4 - IWMS EXPORT */}
            <div className="relative p-5 bg-card border border-border rounded-sm">
              <div className="absolute top-0 left-0 w-1 h-full bg-violet-500 rounded-l-sm" />
              <div className="flex items-start gap-4">
                <div className="shrink-0 w-10 h-10 rounded-sm bg-violet-950/50 border border-violet-900/30 flex items-center justify-center">
                  <Download className="w-5 h-5 text-violet-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-heading font-bold uppercase tracking-wider text-violet-400 mb-1">
                    IWMS-Ready Export
                  </h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    The turnover package isn't just a PDF binder. It includes a 6-sheet Excel workbook structured
                    for direct import into CAFM/IWMS systems (Archibus, FM:Systems, Planon). Owners go from{' '}
                    <em>closeout complete</em> to <em>facilities management live</em> without re-keying a single asset.
                  </p>
                </div>
              </div>
            </div>

            {/* Differentiator 5 - CHASE AUTOMATION */}
            <div className="relative p-5 bg-card border border-border rounded-sm">
              <div className="absolute top-0 left-0 w-1 h-full bg-amber-500 rounded-l-sm" />
              <div className="flex items-start gap-4">
                <div className="shrink-0 w-10 h-10 rounded-sm bg-amber-950/50 border border-amber-900/30 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-amber-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-heading font-bold uppercase tracking-wider text-amber-400 mb-1">
                    Intelligent Chase Automation
                  </h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    SOP-based escalation cadence (Day 7 → 14 → 21 → retainage hold) with AI-drafted professional
                    emails per responsible party. PMs don't write chase letters — they review and send.
                  </p>
                </div>
              </div>
            </div>

          </div>
        </Section>

        {/* Modules */}
        <Section>
          <SectionTitle icon={Zap} color="text-emerald-400">Platform Modules</SectionTitle>
          <div className="space-y-3">

            <ModuleCard
              title="Drawing Intelligence"
              subtitle="Floor plan PDF → AI-detected rooms → CAD-quality export"
              icon={ScanLine}
              color="border-blue-900/50"
            >
              <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
                Upload a floor plan PDF. The system detects every room polygon using computer vision,
                then AI classifies each space (office, corridor, mechanical, restroom) with confidence scores.
                A human reviewer confirms or corrects in a CAD-quality editor with snap-to-wall precision. Export clean DXF/PDF.
              </p>
              <div className="flex flex-wrap gap-1.5">
                <FeaturePill>CV Polygon Detection</FeaturePill>
                <FeaturePill>GPT-4o Classification</FeaturePill>
                <FeaturePill>Snap-to-Wall Editing</FeaturePill>
                <FeaturePill>Batch Auto-Label</FeaturePill>
                <FeaturePill>Confidence Scoring</FeaturePill>
                <FeaturePill>Auto-Merge Overlaps</FeaturePill>
                <FeaturePill>SSE Live Progress</FeaturePill>
                <FeaturePill>DXF/PDF Export</FeaturePill>
              </div>
            </ModuleCard>

            <ModuleCard
              title="Smart Intake"
              subtitle="Parse project docs from Day 1 — sub lists, RFIs, drawings, change orders"
              icon={FileSearch}
              color="border-cyan-900/50"
            >
              <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
                Don't wait until project end to start closeout. Upload sub lists, RFI logs, drawing sets, and change orders
                from Day 1. AI extracts equipment tags, model numbers, and change impacts using hybrid text+image analysis
                with real-time SSE progress bars. Change orders automatically flag affected equipment for VE reconciliation.
              </p>
              <div className="flex flex-wrap gap-1.5">
                <FeaturePill>Sub List Parser</FeaturePill>
                <FeaturePill>RFI Log Parser</FeaturePill>
                <FeaturePill>Hybrid Drawing Extraction</FeaturePill>
                <FeaturePill>Change Order Tracker</FeaturePill>
                <FeaturePill>VE Impact Tracking</FeaturePill>
                <FeaturePill>SSE Progress Bars</FeaturePill>
              </div>
            </ModuleCard>

            <ModuleCard
              title="Construction Closeout SOP"
              subtitle="G703 import → AI filing → gap reports → chase emails → quality gates"
              icon={ClipboardCheck}
              color="border-primary/30/50"
            >
              <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
                Import the G703 Schedule of Values (manual, CSV, or AI reads the PDF).
                Auto-generates every CSI trade division with 9 standard folders (A-I).
                Applicability Wizard marks what's required vs. N/A per trade. AI classifies and files every uploaded document.
              </p>
              <div className="flex flex-wrap gap-1.5">
                <FeaturePill>G703 AI Reader</FeaturePill>
                <FeaturePill>AI Document Classification</FeaturePill>
                <FeaturePill>9-Folder CBC Structure</FeaturePill>
                <FeaturePill>Applicability Wizard</FeaturePill>
                <FeaturePill>Gap Reports</FeaturePill>
                <FeaturePill>Chase Email Generation</FeaturePill>
                <FeaturePill>3-Gate Quality Review</FeaturePill>
                <FeaturePill>Warranty Calendar</FeaturePill>
                <FeaturePill>Stakeholder Registry</FeaturePill>
                <FeaturePill>Pipeline Templates</FeaturePill>
              </div>
            </ModuleCard>

            <ModuleCard
              title="AI Asset & Equipment Intelligence"
              subtitle="The differentiator — AI finds your warranty & O&M docs so subs don't have to"
              icon={Cpu}
              color="border-amber-900/50"
              defaultOpen={true}
            >
              <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
                Instead of waiting for sub-contractors to upload warranty certificates and O&M manuals (they won't),
                the system does it for them — with <span className="text-emerald-400 font-medium">real, verified manufacturer links</span>.
              </p>
              <div className="space-y-3">
                {[
                  { step: '1', title: 'Scan Documents', desc: 'AI reads the project\'s division structure and drawings to extract a complete equipment list — make, model, type, CSI code, location, quantity.', icon: ScanLine, color: 'text-emerald-400' },
                  { step: '2', title: 'Google Search + AI Verify', desc: 'For every piece of equipment with a known manufacturer, AI searches Google for the actual product page, warranty registration, O&M manual PDF, and spec sheet. GPT-4o reviews every result — only real URLs from the manufacturer\'s domain make it through.', icon: Globe, color: 'text-primary', highlight: true },
                  { step: '3', title: 'Build PM Program', desc: 'AI generates manufacturer-specific preventive maintenance schedules — tasks, frequencies, estimated hours, materials with vendor procurement links (Grainger, McMaster, Ferguson).', icon: CalendarCheck, color: 'text-blue-400' },
                  { step: '4', title: 'VE Reconcile', desc: 'At project end, AI reviews the equipment list and flags anything that was Value Engineered out or duplicated. Confirm with one click.', icon: ArrowRightLeft, color: 'text-amber-400' },
                  { step: '5', title: 'Cx Handoff', desc: 'Upload commissioning documents (pre-functional, functional, TAB, startup reports) directly to each asset for a turnkey client handoff.', icon: ClipboardCheck, color: 'text-violet-400' },
                ].map(s => (
                  <div key={s.step} className={`flex gap-3 px-3 py-2.5 rounded-sm ${
                    s.highlight
                      ? 'bg-emerald-950/20 border border-emerald-900/30'
                      : 'bg-secondary/20 border border-border/50'
                  }`}>
                    <div className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                      s.highlight ? 'bg-emerald-900/50' : 'bg-secondary'
                    }`}>
                      <span className={`text-[10px] font-mono font-bold ${s.highlight ? 'text-emerald-400' : 'text-muted-foreground'}`}>{s.step}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <s.icon className={`w-3.5 h-3.5 ${s.color}`} />
                        <p className="text-xs font-heading font-bold uppercase tracking-wider text-foreground">{s.title}</p>
                        {s.highlight && (
                          <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-emerald-950/40 border border-emerald-900/40 text-emerald-500">
                            SERPER + GPT-4o
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-muted-foreground leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </ModuleCard>

            <ModuleCard
              title="Client Handoff Export"
              subtitle="PDF + Excel + ZIP — turnover package ready for owner and IWMS import"
              icon={Package}
              color="border-violet-900/50"
            >
              <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
                Generate a complete turnover package with one click. The PDF report includes cover sheet, asset registry,
                warranty schedule, division matrix, remaining gaps, stakeholders, and change orders. The Excel workbook
                is structured for direct CAFM/IWMS import. The ZIP bundles everything with manifests and a README.
                Before publishing, the owner/admin sees a review summary of open items and signs off each section — a
                timestamped reviewer trail is kept in the Audit tab.
              </p>
              <div className="flex flex-wrap gap-1.5">
                <FeaturePill>PDF Turnover Report</FeaturePill>
                <FeaturePill>IWMS Import Workbook</FeaturePill>
                <FeaturePill>Complete ZIP Package</FeaturePill>
                <FeaturePill>Pre-Publish Review Gate</FeaturePill>
                <FeaturePill accent>Archibus / FM:Systems / Planon</FeaturePill>
              </div>
            </ModuleCard>

          </div>
        </Section>

        {/* 8 AI Agents */}
        <Section>
          <SectionTitle icon={Bot} color="text-cyan-400">8 AI Agents in Production</SectionTitle>
          <div className="grid grid-cols-2 gap-2">
            {agents.map(a => (
              <div key={a.num} className={`flex items-start gap-3 px-3 py-2.5 rounded-sm border ${
                a.highlight
                  ? 'bg-emerald-950/10 border-emerald-900/30'
                  : 'bg-card border-border'
              }`}>
                <div className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${
                  a.highlight ? 'bg-emerald-900/50' : 'bg-secondary'
                }`}>
                  <span className="text-[9px] font-mono font-bold text-muted-foreground">{a.num}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <a.icon className={`w-3 h-3 ${a.color}`} />
                    <p className="text-xs font-heading font-bold uppercase tracking-wider text-foreground/80">{a.name}</p>
                    {a.highlight && (
                      <span className="text-[8px] font-mono px-1 py-0.5 rounded bg-emerald-950/40 border border-emerald-900/40 text-emerald-500">
                        GOOGLE VERIFIED
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{a.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Who Buys This */}
        <Section>
          <SectionTitle icon={Users} color="text-blue-400">Built For</SectionTitle>
          <div className="grid grid-cols-3 gap-3">
            {buyers.map((b, i) => (
              <div key={i} className="flex flex-col items-center text-center px-4 py-5 bg-card border border-border rounded-sm">
                <div className="w-10 h-10 rounded-sm bg-muted/50 flex items-center justify-center mb-3">
                  <b.icon className={`w-5 h-5 ${b.color}`} />
                </div>
                <p className="text-xs font-heading font-bold uppercase tracking-wider text-foreground mb-1">{b.role}</p>
                <p className="text-[10px] text-muted-foreground leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Comparison */}
        <Section>
          <SectionTitle icon={BarChart3} color="text-violet-400">Head-to-Head Comparison</SectionTitle>
          <div className="bg-card border border-border rounded-sm overflow-hidden">
            <div className="grid grid-cols-[1fr_100px_140px] px-4 py-2.5 border-b border-border bg-secondary/30">
              <span className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Capability</span>
              <span className="text-[10px] font-heading uppercase tracking-widest text-primary text-center">FMiQ Intel</span>
              <span className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground text-center">Procore / Kahua / Newforma</span>
            </div>
            {comparison.map((c, i) => (
              <div key={i} className={`grid grid-cols-[1fr_100px_140px] px-4 py-2 border-b border-border/30 last:border-0 ${
                c.key ? 'bg-emerald-950/10' : i % 2 === 0 ? '' : 'bg-secondary/10'
              }`}>
                <span className={`text-xs ${c.key ? 'text-emerald-300 font-medium' : 'text-foreground/80'}`}>{c.capability}</span>
                <div className="flex justify-center">
                  {c.us === true ? (
                    <CheckCircle2 className={`w-4 h-4 ${c.key ? 'text-emerald-400' : 'text-emerald-400'}`} />
                  ) : (
                    <span className="text-xs text-muted-foreground">{String(c.us)}</span>
                  )}
                </div>
                <div className="flex justify-center">
                  {c.them === false ? (
                    <XCircle className="w-4 h-4 text-red-400/40" />
                  ) : (
                    <span className="text-[10px] text-muted-foreground font-mono">{String(c.them)}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Bottom Line / The Pitch */}
        <Section>
          <div className="relative p-6 bg-gradient-to-br from-[#1a1206] to-[#0B1120] border border-primary/30 rounded-sm">
            <div className="absolute top-0 left-0 w-1 h-full bg-primary rounded-l-sm" />
            <h3 className="text-sm font-heading font-bold uppercase tracking-wider text-primary mb-3">The Pitch in 30 Seconds</h3>
            <p className="text-sm text-foreground/80 leading-relaxed">
              Every construction project ends the same way — weeks of chasing subs for warranty certs
              and O&M manuals nobody can find. FMiQ Intel automates the entire closeout process.
              It reads your drawings, builds your equipment list, searches Google for the actual
              manufacturer manuals and warranty pages — <strong className="text-emerald-400">real links, not AI-generated fakes</strong> —
              and packages everything into an owner-ready turnover binder.
            </p>
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
              What used to take your PM <strong className="text-white">60 days of follow-up</strong> now
              runs in the background from Day 1. The facility team inherits a building with every piece
              of equipment cataloged, every warranty term documented, every PM task scheduled, and every
              vendor link ready to click.
            </p>
            <p className="text-base font-heading font-bold text-primary mt-4">
              Day one. Not month six.
            </p>
          </div>
        </Section>

        {/* Footer */}
        <div className="text-center pt-4 pb-8 border-t border-border">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Layers className="w-4 h-4 text-primary" />
            <span className="font-heading font-bold text-xs tracking-wider uppercase text-muted-foreground">
              FMiQ<span className="text-primary">Intel</span>
            </span>
          </div>
          <p className="text-[10px] text-muted-foreground">
            Built by FMiQ. Powered by AI. Designed for the people who actually close out buildings.
          </p>
        </div>
      </div>
    </div>
  );
}
